import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { uniqueId } from 'lodash/fp';
import RenderInBody from 'components/shared/render_in_body';
import Dismissable from 'components/shared/dismissable';
import { focusFirstFocusableNode } from '../utilities/focus';

const POPOVER_OFFSET = 8;

class Popover extends React.Component {
  state = {
    inPosition: false,
  };

  popoverRef = createRef(null);

  contentRef = createRef(null);

  activatorContainerRef = createRef(null);

  activatorNode = null;

  id = uniqueId('popover_');

  static propTypes = {
    isHighPriority: PropTypes.bool,
    align: PropTypes.string,
    position: PropTypes.string,
    animate: PropTypes.bool,
  };

  static defaultProps = {
    align: 'left',
    position: 'bottom',
    animate: true,
    isHighPriority: false,
  };

  // Handle all the position of the popover using JS
  // This lets us render the Popover in the body and then position it wherever we like,
  // so that the Popover can be whatever size we want and isn't reliant on complicated CSS
  // to show up in the right position/alignment
  positionElement = () => {
    this.activatorNode = this.activatorContainerRef.current.firstElementChild;
    const popoverNode = this.popoverRef.current;
    if (popoverNode) {
      const { position, align } = this.props;
      const actRect = this.activatorNode.getBoundingClientRect();
      const popoverNodeRect = popoverNode.getBoundingClientRect();
      // handle whether the Popover is above or below activator
      if (position === 'bottom') {
        popoverNode.style.top = `${
          actRect.bottom + window.pageYOffset + POPOVER_OFFSET
        }px`;
        popoverNode.style.bottom = 'auto';
      } else if (position === 'top') {
        // Use popover dom height to determine top position because
        // we can't rely on window.innerHeight or body.clientWidth
        // to give accurate positioning for the popover
        popoverNode.style.top = `${
          actRect.top -
          popoverNodeRect.height +
          window.pageYOffset -
          POPOVER_OFFSET
        }px`;
        popoverNode.style.bottom = 'auto';
      }
      // handle which edge of the Popover is aligned to the edge of the activator
      if (align === 'right') {
        // the right edge of the Popover aligns with the right edge of the activator
        // We use document.body.clientWidth because it adjusts width to handle scrollbars
        // appearing on the right
        popoverNode.style.right = `${
          document.body.clientWidth - actRect.right
        }px`;
        popoverNode.style.left = 'auto';
      } else if (align === 'middle') {
        const leftPosition =
          actRect.left + actRect.width / 2 - popoverNodeRect.width / 2;
        if (leftPosition < 0) {
          popoverNode.style.left = 'auto';
        } else {
          popoverNode.style.left = `${leftPosition}px`;
        }
        popoverNode.style.right = 'auto';
      } else {
        // the left edge of the Popover aligns with the left edge of the activator
        popoverNode.style.left = `${actRect.left}px`;
        popoverNode.style.right = 'auto';
      }
    }
  };

  setAccessibilityAttributes = () => {
    const { id } = this;
    if (!this.activatorNode) return;
    this.activatorNode.setAttribute('aria-controls', id);
    this.activatorNode.setAttribute('aria-owns', id);
    this.activatorNode.setAttribute('aria-haspopup', 'true');
    this.activatorNode.setAttribute(
      'aria-expanded',
      String(this.props.visible),
    );
  };

  focusContent = () => {
    // We need to focus the Popover, but have to wait until the Popover has appeared
    // the setTimeout causes this to focus once everything else is done rendering
    if (this.contentRef.current) {
      this.contentRef.current.focus();
    }
  };

  // Handles the case where you move the focus out of the Popover. In that situation we should re-focus the activator
  handleFocusOut = event => {
    event.preventDefault();
    this.props.onClose();
    focusFirstFocusableNode(this.activatorContainerRef.current);
  };

  setInPosition = () => {
    this.setState({ inPosition: true });
  };

  componentDidMount() {
    this.positionElement();
    this.setInPosition();
    window.addEventListener('resize', this.positionElement);
  }

  componentDidUpdate(nextProps) {
    if (
      (this.props.visible && this.props.visible !== nextProps.visible) ||
      (this.props.className &&
        this.props.className.includes('sticky') !==
          nextProps.className.includes('sticky'))
    ) {
      setTimeout(() => {
        this.positionElement();
        this.focusContent();
      }, 0);
    }
    // this.setAccessibilityAttributes();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.positionElement);
  }

  render() {
    const {
      activator,
      visible,
      children,
      align,
      position,
      animate,
      className,
      disableTabIndex,
    } = this.props;
    const popoverClasses = classnames(
      'Popover',
      `Popover--${align}`,
      `Popover--${position}`,
      {
        'Popover--animate': animate,
        'Popover--positioned': this.state.inPosition,
        'Popover--highPriority': this.props.isHighPriority,
      },
      className,
    );
    return (
      <div
        className={classnames(this.props.rootClassName)}
        ref={this.activatorContainerRef}
      >
        {React.Children.only(activator)}
        {visible && (
          <RenderInBody>
            <Dismissable
              role="dialog"
              id={this.id}
              innerRef={this.popoverRef}
              className={popoverClasses}
              dismissCallback={this.props.onClose}
              internalDismissCallback={this.props.onSelect}
            >
              {/* These divs are used to close the Popover after you have tabbed through all the options. */}
              {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
              {!disableTabIndex && (
                <div tabIndex={0} onFocus={this.handleFocusOut} />
              )}
              <div
                // eslint-enable jsx-a11y/no-noninteractive-tabindex
                tabIndex={disableTabIndex ? null : -1}
                ref={this.contentRef}
                className="Popover-content"
              >
                {children}
              </div>
              {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
              {!disableTabIndex && (
                <div tabIndex={0} onFocus={this.handleFocusOut} />
              )}
              {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
            </Dismissable>
          </RenderInBody>
        )}
      </div>
    );
  }
}

export default Popover;
